import React, { FocusEvent, MouseEvent, ReactElement, forwardRef, Ref, createRef } from "react";
import classNames from "classnames";
import { observer } from "mobx-react";
import { Transition } from "react-transition-group";
import { observable, action } from "mobx";
import TextInput, { TextInputSize, TextInputType, TextInputTheme, TextInputVariant } from "../TextInput/TextInput";
import { Search, Close } from "../../icons";
import IconButton from "../IconButton/IconButton";
import { ButtonSize, ButtonTheme } from "../Button/Button";
import { AnimationDuration } from "../../../styles/Animations";
import forceReflow from "../../../utils/forceReflow";

export interface ISearchInputProps {
  /**
   * Search input labels are visually hidden, but must be provided for a11y
   */
  label: string;
  /**
   * Determines the height and spacing of the input
   */
  size?: TextInputSize.Small | TextInputSize.Medium;
  /**
   * Handles the clear button being clicked. Typically should set value to ""
   */
  onClear?: () => void;
  /**
   * Light or dark input theme
   */
  theme?: TextInputTheme;
  variant?: TextInputVariant;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
  className?: string;
  /**
   * A unique ID for the input. If not provided, one is generated automatically
   */
  id?: string;
  role?: string;
  startElement?: ReactElement | null;
  endElement?: ReactElement | null;
}

interface SearchInputInnerProps extends ISearchInputProps {
  innerRef?: Ref<any>;
}

@observer
class SearchInput extends React.Component<SearchInputInnerProps> {
  public static defaultProps = {
    theme: TextInputTheme.Light,
    size: TextInputSize.Medium,
    value: "",
  };

  @observable.ref
  private isClearButtonVisible = false;

  private searchInputRef = createRef<HTMLInputElement>();

  public render(): React.ReactElement {
    const {
      label,
      value,
      placeholder,
      onClick,
      onKeyDown,
      onChange,
      onFocus,
      onBlur,
      theme,
      variant,
      className,
      size,
      id,
      role,
      // We pull some of these out to prevent them being passed onto TextInput
      onClear,
      innerRef,
      endElement,
      startElement,
      ...rest
    } = this.props;

    return (
      <div className={classNames(className, "search-input", `search-input--${theme}`)}>
        <TextInput
          labelHidden
          label={label}
          className="search-input__text-input"
          ref={this.mergeRefs}
          size={size}
          startElement={startElement === undefined ? <Search /> : startElement}
          endElement={endElement === undefined ? this.renderClearButton() : endElement}
          type={TextInputType.Search}
          onChange={onChange}
          value={value}
          placeholder={placeholder}
          onClick={onClick}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          theme={theme}
          variant={variant}
          id={id}
          role={role}
          {...rest}
        />
      </div>
    );
  }

  private handleClearClick = (): void => {
    this.searchInputRef.current?.focus();
    this.props.onClear?.();
  };

  private handleClearMouseDown = (event: MouseEvent<HTMLButtonElement>): void => {
    // Prevent mousedown event focusing the button, which causes a flicker
    // as the input is blurred then refocused
    event.preventDefault();
  };

  // Keep track of when the clear button is transitioning so we can
  // remove it after the transition has finished
  @action
  private handleClearEnter = (node: Element): void => {
    forceReflow(node);
    this.isClearButtonVisible = true;
  };

  @action
  private handleClearExited = (): void => {
    this.isClearButtonVisible = false;
  };

  private renderClearButton = (): ReactElement | undefined => {
    const { value, onClear, theme } = this.props;

    if (!onClear) return;

    if (!!value || this.isClearButtonVisible) {
      return (
        <Transition
          appear
          onEnter={this.handleClearEnter}
          onExited={this.handleClearExited}
          in={!!value}
          timeout={AnimationDuration.Medium}
        >
          {(status): ReactElement => (
            <IconButton
              className={classNames("search-input__clear-button", `search-input__clear-button--${status}`)}
              aria-label="Clear search"
              size={ButtonSize.XSmall}
              icon={<Close />}
              theme={theme === TextInputTheme.Dark ? ButtonTheme.Dark : ButtonTheme.Light}
              onClick={this.handleClearClick}
              onMouseDown={this.handleClearMouseDown}
            />
          )}
        </Transition>
      );
    }
  };

  // Because we need to juggle to refs into the one element we use this merging function. It does a little weirdness
  // where we manually update the RefObjects, however this is a necessary evil to avoid juggling whether innerRef exists
  // (and is a function) or not.
  private mergeRefs = (instance: HTMLElement) => {
    if (this.props.innerRef != null) {
      if (typeof this.props.innerRef === "function") {
        this.props.innerRef(instance);
      } else {
        (this.props.innerRef as any).current = instance;
      }
    }

    // We have to do these annoying casts as RefObjects are meant to be read-only
    (this.searchInputRef as any).current = instance;
  };
}

export default forwardRef<HTMLElement, ISearchInputProps>(function textInput(
  props: ISearchInputProps,
  ref: Ref<HTMLElement>,
) {
  return <SearchInput innerRef={ref} {...props} />;
});
